import React, { useCallback, useState } from 'react';
import { Popover } from '@mantine/core';
import classnames from 'clsx';
import { FormattedMessage } from 'react-intl';

import { CommonPopover } from '@shipstation/client-common/components/common-popover';

import { noop } from '../../../default-props/noop';
import { emptyObject } from 'ui-components/default-props/empty';

import { Checkbox } from '../../checkbox';
import { Button } from '../../button';
import { DisplayGridCell } from '../display-grid-cell/index';
import { GroupbyActionPopover } from '../display-grid-body/groupby-actions-popover';

import rowStyle from '../display-grid-body/index.module.less';
import { messages } from '../display-grid-body/messages';
import style from './index.module.css';

const checkboxCol = { id: 'checkbox' };

/**
 * @param {boolean} [props.checked]
 * @param {string} [props.className]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.indeterminate]
 * @param {boolean} [props.isExpanded]
 * @param {(checked: boolean, event: Object) => void} [props.onChange]
 * @param {(event: Object) => void} [props.onClick]
 * @param {(event: Object) => void} [props.onMouseEnter]
 * @param {(event: Object) => void} [props.onMouseLeave]
 * @param {boolean} [props.shouldRenderToggler]
 * @param {boolean} [props.renderCheckbox]
 * @param {() => void} [props.toggleRow]
 * @param {Object} [props.data] - a display grid record or group-by row metadata
 * @param {Set} [props.totalSelectedGroups]
 * @param {() => void} [props.clickTotalSelectGroup]
 */
export const DisplayGridCheckboxCell = React.memo(
  ({
    checked = false,
    className,
    disabled = false,
    indeterminate = false,
    onChange = noop,
    onClick = noop,
    onMouseEnter = noop,
    onMouseLeave = noop,
    shouldRenderToggler = false,
    renderCheckbox = false,
    toggleRow = noop,
    isExpanded = false,
    dataRowId,
    data = emptyObject,
    totalSelectedGroups = null,
    clickTotalSelectGroup = noop,
  }) => {
    const [isGroupByPopoverOpen, setIsGroupbyPopoverOpen] = useState(false);
    const toggleGroupbyPopover = useCallback(() => setIsGroupbyPopoverOpen((isOpen) => !isOpen), []);
    const closeGroupbyPopover = useCallback(() => setIsGroupbyPopoverOpen(false), []);
    const onGroupbyPopoverChange = useCallback((nextOpen) => (nextOpen ? undefined : setIsGroupbyPopoverOpen(false)), []);

    return (
      <DisplayGridCell
        className={classnames(style.checkbox, className, {
          [style['is-groupby-header']]: data.groupById,
        })}
        column={checkboxCol}
        dataRowId={dataRowId}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {shouldRenderToggler && (
          <Button
            className={classnames(rowStyle.expander, {
              [rowStyle.expanded]: isExpanded,
            })}
            data-role='expander'
            onClick={toggleRow}
            theme='link'
          >
            <span>&rsaquo;</span>
          </Button>
        )}
        {renderCheckbox ? (
          <Checkbox checked={checked} disabled={disabled} indeterminate={indeterminate} onChange={onChange} onClick={onClick} />
        ) : (
          <div style={{ content: '', width: '14px' }} />
        )}
        {data.groupByDetails &&
          data.groupByDetails.map((detail, index) => (
            <span key={`groupByRowDetails${index}-${detail.groupByValue}`} className={style['groupby-header-detail']}>
              <b>
                <FormattedMessage {...detail.groupByName} />:
              </b>
              {detail.groupByValue || <FormattedMessage {...messages.none} />}
            </span>
          ))}
        {data.groupById && (
          <div className={style['groupby-header-options']}>
            <span className={style['groupby-header-detail']}>
              {data.selectedRecordCount === 0 && (
                <FormattedMessage {...messages.groupOrdersCount} values={{ totalCount: data.totalRecordCount }} />
              )}
              {data.selectedRecordCount > 0 && (
                <FormattedMessage
                  {...messages.groupOrdersSelectedCount}
                  values={{
                    selectedCount: totalSelectedGroups.has(data.groupById) ? data.totalRecordCount : data.selectedRecordCount,
                    totalCount: data.totalRecordCount,
                  }}
                />
              )}
            </span>
            {checked && data.selectedRecordCount < data.totalRecordCount && !totalSelectedGroups.has(data.groupById) && (
              <Button className={style['groupby-header-detail']} onClick={clickTotalSelectGroup} theme='link'>
                <FormattedMessage {...messages.groupOrdersSelectAll} values={{ totalCount: data.totalRecordCount }} />
              </Button>
            )}
            {data.selectedRecordCount > 0 && (
              <CommonPopover
                classNames={{ dropdown: style['groupby-action-dropdown'] }}
                onChange={onGroupbyPopoverChange}
                opened={isGroupByPopoverOpen}
                position='top'
                withArrow
              >
                <Popover.Target>
                  <Button className={style['groupby-header-detail']} onClick={toggleGroupbyPopover} theme='link'>
                    <FormattedMessage {...messages.groupOrdersMoreOptions} />
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <GroupbyActionPopover
                    closePopover={closeGroupbyPopover}
                    hasSelectedSpilloverItems={totalSelectedGroups.has(data.groupById)}
                  />
                </Popover.Dropdown>
              </CommonPopover>
            )}
          </div>
        )}
      </DisplayGridCell>
    );
  },
);
